import React from 'react';

import { motion, AnimatePresence } from 'motion/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MenuToggleIcon } from '@/components/ui/menu-toggle-icon';
import { HomeIcon, ShoppingCartIcon, UsersIcon, ArrowRightOnRectangleIcon, TicketIcon } from '@/components/icons';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/theme/hooks/useTheme';

const SunIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
    <circle cx="12" cy="12" r="4"></circle>
    <path d="M12 2v2"></path>
    <path d="M12 20v2"></path>
    <path d="m4.93 4.93 1.41 1.41"></path>
    <path d="m17.66 17.66 1.41 1.41"></path>
    <path d="M2 12h2"></path>
    <path d="M20 12h2"></path>
    <path d="m6.34 17.66-1.41 1.41"></path>
    <path d="m19.07 4.93-1.41 1.41"></path>
  </svg>
);

const MoonIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"></path>
  </svg>
);

interface MobileHamburgerMenuProps {
  clubHandle: string | null;
  isAuthenticated?: boolean;
}

export const MobileHamburgerMenu: React.FC<MobileHamburgerMenuProps> = ({ clubHandle, isAuthenticated = false }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { isGuest, user, isAuthenticated: authIsAuthenticated, isLoading: authLoading } = useAuth();

  const isRealUser = !authLoading && (isAuthenticated || authIsAuthenticated) && !isGuest && !user?.is_guest;

  // Close menu when route changes
  React.useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const menuItems = [
    { label: 'Home', path: clubHandle ? `/venues/${clubHandle}` : '/', show: true, icon: HomeIcon, action: undefined },
    { label: 'Cart', path: '/carts', show: true, icon: ShoppingCartIcon, action: undefined },
    { label: 'My Tickets', path: '/tickets', show: isRealUser, icon: TicketIcon, action: undefined },
    ...(isRealUser 
      ? [{ label: 'Profile', path: '/profile', show: true, icon: UsersIcon, action: undefined }]
      : [{ label: 'Login', path: '/login', show: true, icon: ArrowRightOnRectangleIcon, action: undefined }]
    ),
    { 
      label: theme === 'dark' ? 'Light Mode' : 'Dark Mode', 
      path: null, 
      show: true, 
      icon: theme === 'dark' ? SunIcon : MoonIcon,
      action: toggleTheme
    },
  ].filter(item => item.show);

  const handleNavigation = (path: string) => {
    if (path) {
      setIsOpen(false);
      // Start transition prevents the navigation from blocking the menu's closing animation
      React.startTransition(() => {
        navigate(path);
      });
    }
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  // Lock body scroll when menu is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <>
      {/* Hamburger / Close Button */}
      <button
        onClick={toggleMenu}
        className={`p-2 bg-transparent text-white transition-all duration-200 group flex items-center justify-center cursor-pointer ${
          isOpen ? 'fixed top-3 right-4 z-[100]' : 'relative z-[60]'
        }`}
        aria-label={isOpen ? 'Close menu' : 'Toggle menu'}
        aria-expanded={isOpen}
      >
        <MenuToggleIcon open={isOpen} className="w-8 h-8 pointer-events-none" duration={500} />
      </button>

      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={closeMenu}
            className="fixed inset-0 bg-black/60 z-[90]"
            style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
          />
        )}
      </AnimatePresence>

      {/* Bottom Sheet Menu Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.nav
            drag="y"
            dragConstraints={{ top: 0 }}
            dragElastic={{ top: 0, bottom: 0.2 }}
            onDragEnd={(_event, info) => {
              if (info.offset.y > 100 || info.velocity.y > 500) {
                closeMenu();
              }
            }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30,
              mass: 0.8,
            }}
            className="fixed bottom-0 left-0 right-0 max-h-[85vh] bg-[#111111] border-t border-gray-800 z-[95] shadow-2xl rounded-t-[32px] overflow-y-auto overscroll-contain"
            aria-label="Navigation menu"
          >
            {/* Handle/Dragger */}
            <div className="flex justify-center pt-4 pb-2 w-full" onClick={closeMenu}>
              <div className="w-12 h-1.5 bg-gray-600 rounded-full" />
            </div>

            <div className="flex flex-col px-6 pb-12 pt-4">
              <h2 className="text-xl tracking-tight text-white/50 font-medium mb-6 px-2">Menu</h2>
              
              {/* Menu Items */}
              <motion.ul
                initial="closed"
                animate="open"
                exit="closed"
                variants={{
                  open: {
                    transition: {
                      staggerChildren: 0.05,
                      delayChildren: 0.1,
                    },
                  },
                  closed: {
                    transition: {
                      staggerChildren: 0.02,
                      staggerDirection: -1,
                    },
                  },
                }}
                className="space-y-1"
              >
                {menuItems.map((item, index) => {
                  const IconComponent = item.icon;
                  return (
                    <motion.li
                      key={item.path || index}
                      variants={{
                        open: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            type: 'spring',
                            stiffness: 400,
                            damping: 30,
                          },
                        },
                        closed: {
                          opacity: 0,
                          y: 20,
                          transition: { duration: 0.2 }
                        },
                      }}
                    >
                      <button
                        onClick={() => {
                          if (item.action) {
                            item.action();
                            // optionally close menu: closeMenu();
                          } else if (item.path) {
                            handleNavigation(item.path);
                          }
                        }}
                        disabled={!item.path && !item.action}
                        className="w-full flex items-center gap-4 px-4 py-4 text-white hover:bg-white/5 rounded-2xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
                      >
                        <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                          <IconComponent className="w-5 h-5 flex-shrink-0 text-white" />
                        </div>
                        <span className="text-lg font-medium tracking-wide">{item.label}</span>
                      </button>
                    </motion.li>
                  );
                })}
              </motion.ul>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
};
